import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SupplierForm from './SupplierForm';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import { ActiveStatusPill } from '../../../shared/Pill';
import { PlaceholderThumb } from '../../../shared/ProductThumb';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { thumbFileUrl } from '../../../shared/mediaFileUrl';
import { formatAddress } from '../../../shared/formatAddress';
import { Filter as FilterIcon } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { formatNumber, getTenantHomeCurrency } from '../../../shared/numberFormat';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { askConfirm } from '../../../shared/AppDialogs';

// Identifies this screen's saved filters (SavedFilterTabs) from any other
// screen's — same shared saved_filters table/pattern as Customers/All
// Entities.
const FILTER_SCREEN_KEY = 'suppliers';
const DEFAULT_FILTERS = { status: 'all', sync: '' };
const OPTIONAL_COLUMNS = [
  { key: 'currency', label: 'Currency', group: 'Trading' },
  { key: 'exchange_rate', label: "Today's Rate", group: 'Trading' },
  { key: 'address', label: 'Address', group: 'Location & Contact' },
  { key: 'state', label: 'State', group: 'Location & Contact' },
  { key: 'country', label: 'Country', group: 'Location & Contact' },
  { key: 'phone', label: 'Phone', group: 'Location & Contact' },
  { key: 'sync', label: 'Email Sync', group: 'Admin' },
  { key: 'status', label: 'Status', group: 'Admin' },
];
const SYSTEM_COLUMNS = ['Logo', 'Name', 'Actions'];
// The everyday job (ui-architecture.md Column Picker): suppliers are often
// overseas, so Currency and Country over State. Everything else is one
// tick away in Columns.
const DEFAULT_VISIBLE_COLUMNS = ['currency', 'country', 'phone', 'status'];
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Suppliers is a filtered view over Commercial Entities holding the
// Supplier Relationship Type (supplierController.js) — same shape as
// Customers/All Entities, just against /api/suppliers instead of the
// generic commercial-entities endpoints, since that's the one that also
// carries Currency/Exchange Rate. Row click / View both go to the shared
// detail page (CommercialEntityDetailPage.js) — editing addresses/emails/
// phones/etc. happens there, not in an inline form; the inline form here
// is Add-only, since creating a Supplier also opens its exchange rate
// schedule with one entry (see supplierController.addSupplier).
function SupplierTable() {
  const { canCreate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { status, sync: syncFilter } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Free-text search, kept outside DEFAULT_FILTERS/SavedFilterTabs — same
  // split every other list screen uses (search is unbounded free text, not
  // a saved filter value).
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
  const showCurrency = visibleColumns.includes('currency');
  const showExchangeRate = visibleColumns.includes('exchange_rate');
  const showState = visibleColumns.includes('state');
  const showCountry = visibleColumns.includes('country');
  const showAddress = visibleColumns.includes('address');
  const showPhone = visibleColumns.includes('phone');
  const showSync = visibleColumns.includes('sync');
  const showStatus = visibleColumns.includes('status');
  const totalCols = 3 // media, Name, actions
    + OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).length;

  // Ad hoc "Filters" panel — draft is null while closed, same pattern as
  // every other list screen's Filters button.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All suppliers</option>
          <option value="active">Active suppliers</option>
          <option value="inactive">Deactivated suppliers</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Email Sync</label><br />
        <select className="styled-input" value={draft.sync} onChange={(e) => setDraftField('sync', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          <option value="synced">On</option>
          <option value="not_synced">Off</option>
        </select>
      </div>
    </>
  );

  useDefaultPageSize(setLimit);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, status, syncFilter, page, limit]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { status, page, limit };
      if (search) params.search = search;
      if (syncFilter) params.sync = syncFilter;
      const res = await axios.get('/api/suppliers', { params });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  const handleAdd = () => {
    setShowForm(true);
  };

  const handleView = (item) => {
    navigate(`/commercial-entities/${item.id}`, { state: { browseIds: rows.map((r) => r.id), origin: { label: 'Suppliers', path: '/suppliers' } } });
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate supplier "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/suppliers/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/suppliers/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  // Deactivated Suppliers only — the shared Commercial Entity delete refuses
  // one still in use (Products, Shipping Services, Rebate Rules).
  const handleDelete = async (item) => {
    if (!(await askConfirm(`Permanently delete supplier "${item.name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/commercial-entities/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      await axios.post('/api/suppliers', data);
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Suppliers</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: showForm ? 'not-allowed' : 'pointer', opacity: showForm ? 0.6 : 1 }}
            >
              Add Supplier
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <input
            className="styled-input"
            placeholder="Search name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 200 }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
          )}
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
        <ColumnPicker
          screenKey={FILTER_SCREEN_KEY}
          columns={OPTIONAL_COLUMNS}
          defaultVisible={DEFAULT_VISIBLE_COLUMNS}
          onChange={setVisibleColumns}
          tabColumns={tabColumns}
          systemColumns={SYSTEM_COLUMNS}
        />
      </div>

      <div style={{ margin: '10px 24px 0' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          tabColumns={tabColumns}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the supplier list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <SupplierForm
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '32%' }} />
            {showCurrency && <col style={{ width: '14%' }} />}
            {showExchangeRate && <col style={{ width: '14%' }} />}
            {showAddress && <col style={{ width: '12%' }} />}
            {showState && <col style={{ width: '9%' }} />}
            {showCountry && <col style={{ width: '14%' }} />}
            {showPhone && <col style={{ width: '15%' }} />}
            {showSync && <col style={{ width: '9%' }} />}
            {showStatus && <col style={{ width: '10%' }} />}
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Name</th>
              {showCurrency && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Currency</th>}
              {showExchangeRate && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'right' }}>Today's Rate</th>}
              {showAddress && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Address</th>}
              {showState && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>State</th>}
              {showCountry && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Country</th>}
              {showPhone && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Phone</th>}
              {showSync && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Email Sync</th>}
              {showStatus && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Status</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm) handleView(item); }}
                style={{ cursor: showForm ? 'default' : 'pointer' }}
                title={showForm ? undefined : 'Click to view'}
              >
                <td className="cell-media" style={{ padding: '6px 8px' }}>
                  {item.logo ? (
                    <img src={thumbFileUrl(item.logo.id)} alt="" style={{ display: 'block', margin: '0 auto', width: 28, height: 28, objectFit: 'contain', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, padding: 2 }} />
                  ) : (
                    <PlaceholderThumb size={28} />
                  )}
                </td>
                <td className="cell-title" style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.name}</td>
                {showCurrency && (
                  <td data-label="Currency" style={{ padding: '6px 8px', fontSize: 14 }} title={item.currency ? undefined : "No currency set — set it on the Supplier's Currency card"}>
                    {item.currency ? `${item.currency.currency_code} - ${item.currency.currency_name}` : '—'}
                  </td>
                )}
                {showExchangeRate && (
                  <td data-label="Today's Rate" style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }} title={item.exchange_rate === null ? (item.currency_code ? 'No reference rate could be retrieved for this currency' : 'No currency set') : `Today's reference rate, ${getTenantHomeCurrency()} per ${item.currency_code}`}>
                    {item.exchange_rate === null ? '—' : formatNumber(item.exchange_rate, { decimals: 6 })}
                  </td>
                )}
                {showAddress && <td data-label="Address" style={{ padding: '6px 8px', fontSize: 14 }}>{formatAddress(item.primary_address) || '—'}</td>}
                {showState && <td data-label="State" style={{ padding: '6px 8px', fontSize: 14 }}>{item.primary_address?.state || '—'}</td>}
                {showCountry && <td data-label="Country" style={{ padding: '6px 8px', fontSize: 14 }}>{item.primary_address?.country || '—'}</td>}
                {showPhone && <td data-label="Phone" style={{ padding: '6px 8px', fontSize: 14 }}>{item.phones?.length ? item.phones.map((p) => p.phone_number).join(', ') : '—'}</td>}
                {showSync && (
                  <td data-label="Email Sync" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <ActiveStatusPill active={item.sync} activeLabel="On" inactiveLabel="Off" />
                  </td>
                )}
                {showStatus && (
                  <td data-label="Status" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 13 }}>
                    <ActiveStatusPill active={item.is_active} />
                  </td>
                )}
                <td className="cell-actions" style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'view', label: 'View', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleView(item) },
                      { key: 'cost-prices', label: 'Cost Price Schedule', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/suppliers/${item.id}/cost-prices`) },
                      item.is_active
                        ? { key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }
                        : { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                      !item.is_active && { key: 'delete', label: 'Delete', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDelete(item) },
                    ].filter(Boolean)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Suppliers"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default SupplierTable;
